//#region IMPORT
/** Components Import */
import {
  Unstable_Grid2 as Grid,
  InputAdornment,
  TextField,
  Button as MuiButton,
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { AuthButton, Button, Dialog } from '../../../../Components/MUIs';
import { Check as CheckIcon, Person as PersonIcon } from '@mui/icons-material';
/** AG-grid Import */
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
/** Icons Import */
import { Search as SearchIcon } from '@mui/icons-material';
import { BusinessGridCommon } from '../../../../Components/BusinessGridCommon';
import GridFunctions from '../../../../Services/agGrid/ag-Grid-functions';
import { AgGridReact } from 'ag-grid-react';
import { ExcelGridCommon } from '../../../../Components/ExcelGridCommon';
import {
  getColumnDefs1,
  getColumnDefs2,
  getColumnDefs3,
} from './MEM_BASIC_ColumnDefs';
// (엑셀 업로드 키/비교는 MEM_BASICF에서 DEP_BASICF 패턴으로 처리)
import { useSelector } from 'react-redux';
import { setSearchText } from '../../../../Reducer/SearchReducer';
import { useDispatch } from 'react-redux';
import Icon from '../../../../Assets/Icon/CustomIcons';
//#endregion
function MEM_BASIC(props) {
  //#region 웹
  const { companyInfo } = useSelector((state) => state.BaseInfo);
  const contractMemberCount = Number(
    companyInfo?.[0]?.contractMemberCount ?? 0
  );
  const isUnder300 =
    Number.isFinite(contractMemberCount) && contractMemberCount < 300;
  const dispatch = useDispatch();
  // 사용자정보 컬럼
  const columnDefs1 = getColumnDefs1(props);
  // 부서정보
  const columnDefs2 = getColumnDefs2();
  // 엑셀 업로드 컬럼
  const columnDefs3 = getColumnDefs3();
  const searchInfo = useSelector((state) => state.SearchInfo);
  //#endregion

  //#region 함수
  const handleDepartmentSelection = (selectedData) => {
    const api = props.gridRef1?.current?.api;
    if (!api) return;

    const allNodes = [];
    api.forEachNode((node) => allNodes.push(node));

    let targetRowNode;

    if (props.clickRowId) {
      // 기존 행 클릭해서 선택한 경우
      targetRowNode = allNodes.find(
        (node) => node.rowIndex === props.clickRowId.rowIndex
      );
    } else {
      // 새 행 추가된 경우
      targetRowNode = allNodes.find((node) => node.data?.newYn === 'Y');
    }

    if (targetRowNode && selectedData) {
      targetRowNode.setDataValue('depCd', selectedData.depCd);
      targetRowNode.setDataValue('depNm1', selectedData.depNm1);
      targetRowNode.setDataValue('depNm2', selectedData.depNm2);
      targetRowNode.setDataValue('depNm3', selectedData.depNm3);
      targetRowNode.setDataValue('depNm4', selectedData.depNm4);

      api.setFocusedCell(targetRowNode.rowIndex, 'depNm1');
      api.startEditingCell({
        rowIndex: targetRowNode.rowIndex,
        colKey: 'depNm1',
      });
    }

    props.setClickRowId(null);
  };

  //#endregion

  //#region 직책 선택 관련 함수
  // 기본 직책 목록 (Search5 데이터가 없을 때 사용)

  // 실제 사용할 직책 목록 (Search5 데이터가 있으면 그것을, 없으면 기본 목록)
  // 새로 추가한 직책도 포함, 삭제된 직책은 제외
  const getPositionList = () => {
    let baseList = [];
    if (props.positionList && props.positionList.length > 0) {
      baseList = props.positionList.map(
        (item) => item.CpNm || item.cpNm || item
      );
    } else {
      baseList = props.DEFAULT_POSITION_LIST;
    }

    // 새로 추가한 직책 추가 (중복 제거)
    const allPositions = [...baseList];
    props.newlyAddedPositions?.forEach((newPos) => {
      if (!allPositions.includes(newPos)) {
        allPositions.push(newPos);
      }
    });

    // 삭제된 직책 제외
    const deleted = props.deletedPositions || [];
    const filteredPositions = allPositions.filter(
      (pos) => !deleted.includes(pos)
    );

    return filteredPositions;
  };

  const normalizePosition = (value) => String(value ?? '').trim();
  const PAIR_SAFETY_OWNER = ['안전보건관리책임자', '안전보건총괄책임자'];
  const PAIR_UNDER_300 = ['안전관리자', '보건관리자']; // 300인 미만일 때만 허용
  const PAIR_MANAGER = ['관리감독자', '안전보건관리담당자'];

  const isSamePair = (a, b, pair) => {
    const x = normalizePosition(a);
    const y = normalizePosition(b);
    if (!x || !y) return false;
    return pair.includes(x) && pair.includes(y) && x !== y;
  };

  const isAllowedDuplicatePair = (a, b) => {
    if (isSamePair(a, b, PAIR_SAFETY_OWNER)) return true;
    if (isSamePair(a, b, PAIR_MANAGER)) return true;
    if (isUnder300 && isSamePair(a, b, PAIR_UNDER_300)) return true;
    return false;
  };

  // 기본 1개 선택, 예외적으로 특정 조합은 2개 선택 허용
  const togglePositionSelection = (currentSelections, clickedPosition) => {
    const clicked = normalizePosition(clickedPosition);
    const current = (currentSelections || [])
      .map(normalizePosition)
      .filter(Boolean);

    if (!clicked) return current;

    // 이미 선택된 항목이면 해제
    if (current.includes(clicked)) {
      return current.filter((positionName) => positionName !== clicked);
    }

    // 아무것도 없으면 추가
    if (current.length === 0) return [clicked];

    // 1개 선택 상태: 허용 조합이면 2개까지, 아니면 교체
    if (current.length === 1) {
      const only = current[0];
      if (isAllowedDuplicatePair(only, clicked)) return [only, clicked];
      return [clicked];
    }

    // 2개 선택 상태에서 제3 항목 클릭: 허용 조합 유지 가능하면 해당 조합만 남기고 교체, 아니면 단독으로 교체
    const [a, b] = current;
    if (isAllowedDuplicatePair(a, clicked)) return [a, clicked];
    if (isAllowedDuplicatePair(b, clicked)) return [b, clicked];
    return [clicked];
  };

  // 직책 선택 핸들러
  const handlePositionSelection = (selectedPositions) => {
    const api = props.gridRef1?.current?.api;
    if (!api) return;

    const allNodes = [];
    api.forEachNode((node) => allNodes.push(node));

    let targetRowNode;

    if (props.clickRowId) {
      // 기존 행 클릭해서 선택한 경우
      targetRowNode = allNodes.find(
        (node) => node.rowIndex === props.clickRowId.rowIndex
      );
    } else {
      // 새 행 추가된 경우
      targetRowNode = allNodes.find((node) => node.data?.newYn === 'Y');
    }

    if (targetRowNode) {
      targetRowNode.setDataValue(
        'position',
        (selectedPositions || [])
          .map(normalizePosition)
          .filter(Boolean)
          .join(',')
      );

      api.setFocusedCell(targetRowNode.rowIndex, 'position');
    }

    props.setClickRowId(null);
  };

  //#endregion

  //#region 총괄관리자 사업장/부서 선택 모달 공통 렌더
  const RenderCustDepPickerModal = ({
    open,
    title,
    confirmLabel,
    onConfirm,
    onCancel,
    custItems,
    selectedCustCd,
    setSelectedCustCd,
    depItems,
    selectedDepCd,
    setSelectedDepCd,
  }) => {
    const normalize = (v) => String(v ?? '').trim();
    const hasCust = !!normalize(selectedCustCd);
    const hasDeps = Array.isArray(depItems) && depItems.length > 0;

    const getCustKey = (item) =>
      normalize(item?.custCd ?? item?.CustCd ?? item?.value ?? '');
    const getCustLabel = (item) =>
      item?.custNm ?? item?.CustNm ?? item?.name ?? getCustKey(item);

    const getDepKey = (item) =>
      normalize(item?.depCd ?? item?.DepCd ?? item?.value ?? '');
    const getDepLabel = (item) => {
      const parts = [item?.depNm1, item?.depNm2, item?.depNm3, item?.depNm4]
        .map((x) => normalize(x))
        .filter(Boolean);
      return parts.length ? parts.join(' > ') : getDepKey(item);
    };

    const renderDepEmptyMessage = () => {
      if (!hasCust) return '사업장을 먼저 선택하세요.';
      if (!hasDeps) return '조회된 부서가 없습니다.';
      return '';
    };

    const depEmptyMsg = renderDepEmptyMessage();

    return (
      <div role="presentation" className="MuiDialog-root MuiModal-root">
        <Dialog
          title={title}
          open={open}
          onClose={onCancel}
          contentSx={{ display: 'flex', flexDirection: 'column', p: 0 }}
          fullWidth
          useSaveButton={false}
          useCloseButton={false}
          maxWidth="md"
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <MuiButton
                variant="contained"
                onClick={onConfirm}
                sx={{ minWidth: 96, height: 36, bgcolor: '#1976d2' }}
              >
                {confirmLabel}
              </MuiButton>
              <MuiButton
                variant="contained"
                onClick={onCancel}
                sx={{ minWidth: 96, height: 36, bgcolor: '#D32F2F' }}
              >
                취소
              </MuiButton>
            </Box>
          }
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  minHeight: '300px',
                  maxHeight: '400px',
                  borderRight: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1 }}>
                  사업장 선택
                </Typography>
                <List sx={{ p: 0 }}>
                  {(custItems || []).map((item, index) => {
                    const key = getCustKey(item);
                    const label = getCustLabel(item);
                    const isSelected = normalize(selectedCustCd) === key;

                    return (
                      <ListItem
                        key={key || index}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: isSelected && '1px solid #1976d2',
                          bgcolor: isSelected ? '#e3f2fd' : 'transparent',
                        }}
                      >
                        <ListItemButton
                          onClick={() => {
                            setSelectedCustCd(isSelected ? '' : key);
                            // 사업장 변경 시 부서는 즉시 초기화(잔상 방지)
                            setSelectedDepCd('');
                          }}
                          sx={{ py: 1.5, px: 2 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              flex: 1,
                            }}
                          >
                            {isSelected ? (
                              <CheckIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                            ) : (
                              <Icon.Cust
                                width="20"
                                height="20"
                                color="rgba(0, 0, 0, 0.54)"
                              />
                            )}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: '0.95rem',
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected ? '#1976d2' : 'inherit',
                                }}
                              >
                                {label}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  {(!custItems || custItems.length === 0) && (
                    <Typography
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      조회된 사업장이 없습니다.
                    </Typography>
                  )}
                </List>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  minHeight: '300px',
                  maxHeight: '400px',
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1 }}>
                  부서 선택
                </Typography>
                <List sx={{ p: 0 }}>
                  {hasCust &&
                    (depItems || []).map((item, index) => {
                      const key = getDepKey(item);
                      const label = getDepLabel(item);
                      const isSelected = normalize(selectedDepCd) === key;

                      return (
                        <ListItem
                          key={key || index}
                          disablePadding
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: isSelected && '1px solid #1976d2',
                            bgcolor: isSelected ? '#e3f2fd' : 'transparent',
                          }}
                        >
                          <ListItemButton
                            onClick={() => setSelectedDepCd(isSelected ? '' : key)}
                            disabled={!hasCust}
                            sx={{ py: 1.5, px: 2 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flex: 1,
                              }}
                            >
                              {isSelected ? (
                                <CheckIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                              ) : (
                                <Icon.Cust
                                  width="20"
                                  height="20"
                                  color="rgba(0, 0, 0, 0.54)"
                                />
                              )}
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: '0.95rem',
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? '#1976d2' : 'inherit',
                                  }}
                                >
                                  {label}
                                </Typography>
                              </Box>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}

                  {!!depEmptyMsg && (
                    <Typography
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        color: 'text.secondary',
                        fontSize: '0.9rem',
                      }}
                    >
                      {depEmptyMsg}
                    </Typography>
                  )}
                </List>
              </Box>
            </Box>
          }
        />
      </div>
    );
  };
  //#endregion

  return (
    <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid xs={4} md={12}>
        <AuthButton
          menuId="Member"
          saveYn="Y"
          applyYn="Y"
          printYn="N"
          applyName="계정 활성화"
          applyCancleName="계정 비활성화"
          other1={
            props.userInfo.authCd === 'BUSINESS_OWNER' && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Button
                  name="총괄관리자 사업장 소속 선택"
                  sx={{ color: '#2196F3', borderColor: '#2196F3' }}
                  size={''}
                  variant="outlined"
                  onClick={() => {
                    props.setModalToggle3(true);
                    props.search6();
                  }}
                />
                <Button
                  name="총괄관리자 선임"
                  sx={{ color: '#2196F3', borderColor: '#2196F3' }}
                  size={''}
                  variant="outlined"
                  onClick={() => props.AppointBusinessOwner()}
                />
              </Box>
            )
          }
          saveClick={() => {
            if (!props.validateSelection()) return;

            const isExcel = props.changedRowData1?.some((r) => r.excel === 'Y');

            if (isExcel && !props.validateExcelSave()) return;

            props.SaveLogic1();
          }}
          applyClick={() => {
            if (!props.validateSelection()) return;
            props.approveRef.current = '0';
            props.SaveLogic3();
          }}
          applyCancleClick={() => {
            if (!props.validateSelection()) return;

            props.approveRef.current = '1';
            props.SaveLogic3();
          }}
          searchClick={(e) => {
            if (!props.validateSelection()) return;

            dispatch(setSearchText(e));
            props.gridRef1.current.api.setQuickFilter(e);
          }}
          conditions={[
            // ADMIN이면 추가 combo
            ...(props.userInfo.authCd === 'ADMIN'
              ? [
                  {
                    type: 'combo',
                    name: '회사',
                    items: searchInfo.coList,
                    value: searchInfo.coCd,
                    onChange: async (e) => {
                      await props.handleCoChange(e);
                      await props.SearchLogic1();
                    },
                  },
                ]
              : []),
            {
              type: 'combo',
              name: '사업장',
              items: searchInfo.custList,
              value: searchInfo.custCd,
              onChange: async (e) => {
                await props.handleCustChange(e); // 훅을 통해 값 갱신
                await props.SearchLogic1(); // 그리고 조회
              },
            },
          ]}
          grids={
            <>
              <BusinessGridCommon
                getRowId={(params) => {
                  return params.data.no;
                }}
                title1={'사용자정보'}
                deleteYn1={'Y'}
                fileUploadYn1="Y"
                fileDownloadYn1="Y"
                addClick1={(e) => {
                  if (!props.validateSelection()) return; // 검색조건 유효성검사
                  if (!props.validateContractMember()) return; // 계약인원 유효성검사

                  const clickedRowIndex = e?.rowIndex;

                  if (clickedRowIndex !== undefined) {
                    // 기존 row 클릭
                    props.setClickRowIndex1(clickedRowIndex);
                    props.setClickRowId(e);
                  } else {
                    // 새 행 추가
                    GridFunctions.addClick(props.gridRef1, {
                      internalYn: '0',
                    });

                    // 💡 추가 직후 0번 row가 새 row이므로 그걸 임시로 저장
                    const api = props.gridRef1.current?.api;
                    if (api) {
                      setTimeout(() => {
                        const firstRowNode = api.getDisplayedRowAtIndex(0);
                        if (firstRowNode) {
                          props.setClickRowId({
                            rowIndex: firstRowNode.rowIndex,
                            data: firstRowNode.data,
                          });
                        }
                      }, 50);
                    }
                  }

                  props.SearchLogic2();
                  props.setModalToggle1(true);
                }}
                deleteClick1={(e) => {
                  if (!props.validateSelection()) return;

                  const newRowExists = props.selectedRowData1.some(
                    (row) => row.newYn === 'Y'
                  );

                  if (!newRowExists) {
                    props.MarkedForDeletion1(props.selectedRowData1);
                  } else {
                    props.NewYnErrorMsg();
                  }
                }}
                refreshClick1={(e) => {
                  if (!props.validateSelection()) return;

                  props.SearchLogic1();
                }}
                fileUploadClick1={() => {
                  if (!props.validateSelection()) return;
                  if (!props.validateContractMember()) return;

                  props.fileInputRef.current.click();
                }}
                fileDownloadClick1={() => {
                  if (!props.validateSelection()) return;

                  props.ExcelDownload();
                }}
                gridRef={props.gridRef1}
                columnDefs={columnDefs1}
                rowData={props.rowData1}
                setSelectedRowData={props.setSelectedRowData1}
                setChangedRowData={props.setChangedRowData1}
                onCellClicked={(e) => {
                  // 필요 시 특정 행 선택 등 처리
                  const gridApi = props.gridRef1.current.api;
                  const clickedRowIndex = e.rowIndex;

                  const isCheckboxClick = e.event?.target?.type === 'checkbox';

                  if (!isCheckboxClick) {
                    gridApi.ensureIndexVisible(clickedRowIndex, 'middle');
                    gridApi.setFocusedCell(clickedRowIndex, 'coNm');

                    const copied = JSON.parse(JSON.stringify(e.data));
                    props.handleRowSelection(copied, clickedRowIndex);

                    gridApi
                      .getDisplayedRowAtIndex(clickedRowIndex)
                      ?.setSelected(true, false);
                  }
                }}
                onSelectionChanged={() => {
                  const gridApi = props.gridRef1?.current?.api;
                  const selectedNodes = gridApi.getSelectedNodes();
                  const selectedData = selectedNodes.map((n) => n.data);
                  props.selectedRowNosRef.current = new Set(
                    selectedData.map((r) => r.no)
                  );
                  props.setSelectedRowData1(selectedData);
                }}
              />
              <ExcelGridCommon
                gridRef={props.gridRef3}
                rowData={props.excelData}
                columnDefs={columnDefs3}
                rowSelection="multiple"
              />
              <input
                type="file"
                ref={props.fileInputRef}
                style={{ display: 'none' }}
                accept=".xls,.xlsx"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  try {
                    if (file) await props.ExcelUpload1?.(file);
                  } finally {
                    e.target.value = '';
                  }
                }}
              />
            </>
          }
        />
      </Grid>
      {/* 부서정보 팝업 창 */}
      {props.modalToggle1 && (
        <div
          role="presentation"
          className="MuiDialog-root MuiModal-root"
          inert="true"
        >
          <Dialog
            title={
              <>
                부서 목록
                <TextField
                  placeholder="검색"
                  variant="outlined"
                  size="small"
                  sx={{
                    mx: 2,
                    '& .MuiOutlinedInput-root': {
                      paddingRight: 0,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ height: '100%' }}>
                        <MuiButton
                          edge="end"
                          size="small"
                          sx={{
                            borderLeft: 1,
                            borderColor: 'divider',
                            color: 'rgba(0, 0, 0, 0.54)', // 기본 색상
                          }}
                          style={{
                            padding: '0px',
                            borderRadius: '0px',
                            borderTopRightRadius: '8px',
                            borderBottomRightRadius: '8px',
                          }}
                          onClick={() =>
                            props.gridRef2.current.api.setQuickFilter(
                              props.modalSearchText
                            )
                          }
                        >
                          <SearchIcon sx={{ m: 0 }} style={{ margin: '0px' }} />
                        </MuiButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      props.gridRef2.current.api.setQuickFilter(
                        props.modalSearchText
                      );
                    }
                  }}
                  value={props.modalSearchText}
                  onChange={(e) => props.setModalSearchText(e.target.value)}
                />
              </>
            }
            open={props.modalToggle1}
            onClose={(e) => props.setModalToggle1(false)}
            contentSx={{ display: 'flex', flexDirection: 'column' }}
            fullWidth
            saveButtonName={'선택'}
            closeButtonName={'닫기'}
            useSaveButton={true}
            useSaveAfterClose={true}
            maxWidth="md"
            onSave={() => {
              if (props.selectedModalRowData1.length > 0) {
                const selected = props.selectedModalRowData1[0];
                props.onSave?.(selected);
                props.setModalToggle1(false);

                setTimeout(() => {
                  handleDepartmentSelection(selected);
                }, 100);
              }
            }}
            content={
              <div
                className="ag-theme-alpine no-scroll"
                style={{ height: '600px', width: '100%' }}
              >
                <AgGridReact
                  ref={props.gridRef2}
                  animationRows={true}
                  columnDefs={columnDefs2}
                  rowData={props.modalRowData1}
                  defaultColDef={{
                    minWidth: 100,
                    editable: false,
                    resizable: true,
                    sortable: true,
                    filter: true,
                    floatingFilter: true,
                  }}
                  rowSelection="single"
                  onSelectionChanged={() => {
                    GridFunctions.onSelectionChanged(
                      props.gridRef2,
                      (_selected) => {
                        props.setSelectedModalRowData1(_selected);
                      }
                    );
                  }}
                  getRowStyle={(e) => GridFunctions.getRowStyle(e)}
                  onRowDoubleClicked={(e) => {
                    props.setModalToggle1(false); // 모달 닫기

                    setTimeout(() => {
                      handleDepartmentSelection(e.data);
                    }, 100);
                  }}
                />
              </div>
            }
          />
        </div>
      )}
      {/* 직책 선택 팝업 창 */}
      {props.modalToggle2 && (
        <div
          role="presentation"
          className="MuiDialog-root MuiModal-root"
          inert="true"
        >
          <Dialog
            title="안전보건조직 선택"
            open={props.modalToggle2}
            onClose={(e) => {
              props.setModalToggle2(false);
              props.setNewPositionText('');
              props.setSelectedPositions([]);
              // 모달 닫을 때 새로 추가한 직책 목록은 유지 (다음에 다시 열었을 때도 보이도록)
              // 삭제된 직책 목록도 유지 (저장 전까지는 삭제 상태 유지)
            }}
            contentSx={{ display: 'flex', flexDirection: 'column', p: 0 }}
            fullWidth
            useSaveButton={false}
            useCloseButton={false}
            maxWidth="sm"
            actions={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <MuiButton
                  variant="contained"
                  onClick={() => {
                    handlePositionSelection(props.selectedPositions ?? []);
                    props.setModalToggle2(false);
                    props.setNewPositionText('');
                    props.setSelectedPositions([]);
                  }}
                  sx={{
                    minWidth: 96,
                    height: 36,
                    px: 2.5,
                    bgcolor: '#1976d2',
                    '&:hover': {
                      bgcolor: '#1565c0',
                    },
                  }}
                >
                  선택
                </MuiButton>
                <MuiButton
                  variant="contained"
                  onClick={() => {
                    props.setModalToggle2(false);
                    props.setNewPositionText('');
                    props.setSelectedPositions([]);
                  }}
                  sx={{
                    minWidth: 96,
                    height: 36,
                    px: 2.5,
                    bgcolor: '#D32F2F',
                    '&:hover': {
                      bgcolor: '#B71C1C',
                    },
                  }}
                >
                  취소
                </MuiButton>
              </Box>
            }
            content={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                {/* 안내 문구 */}
                <Box
                  sx={{
                    mx: 2,
                    mt: 2,
                    mb: 0,
                    px: 1.5,
                    py: 1.2,
                    borderRadius: 2,
                    bgcolor: '#E3F2FD',
                    border: '1px solid #BBDEFB',
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>
                    안내
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.85rem', color: 'text.secondary' }}
                  >
                    선택 가능한 안전보건조직은 권한에 따라 제한될 수 있습니다.{' '}
                    <br />
                    직책은 기본적으로 1개만 선택 가능하며
                    <br />
                    아래 조합은 중복 선택이 가능합니다.
                    <br />-{' '}
                    <span style={{ fontWeight: 'bold', color: '#ff7070' }}>
                      안전보건관리책임자
                    </span>{' '}
                    +{' '}
                    <span style={{ fontWeight: 'bold', color: '#ff7070' }}>
                      안전보건총괄책임자
                    </span>
                    <br />-{' '}
                    <span style={{ fontWeight: 'bold', color: '#ff7070' }}>
                      관리감독자
                    </span>{' '}
                    +{' '}
                    <span style={{ fontWeight: 'bold', color: '#ff7070' }}>
                      안전보건관리담당자
                    </span>
                    <br />-{' '}
                    <span style={{ fontWeight: 'bold', color: '#ff7070' }}>
                      안전관리자
                    </span>{' '}
                    +{' '}
                    <span style={{ fontWeight: 'bold', color: '#ff7070' }}>
                      보건관리자
                    </span>{' '}
                    {isUnder300
                      ? '(300인 미만 사업장: 중복 선택 가능)'
                      : '(300인 이상 사업장: 중복 선택 불가)'}
                  </Typography>
                </Box>
                {/* 직책 목록 */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    minHeight: '300px',
                    maxHeight: '400px',
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {getPositionList().map((positionName, index) => {
                      const normalize = (v) => String(v ?? '').trim();
                      const posNm = normalize(positionName);

                      const rawAuthNm =
                        props.clickRowId?.data?.authNm ??
                        (() => {
                          const api = props.gridRef1?.current?.api;
                          if (!api) return '';
                          let authNm = '';
                          api.forEachNode((node) => {
                            if (node.data?.newYn === 'Y')
                              authNm = node.data?.authNm ?? '';
                          });
                          return authNm;
                        })();

                      const targetRowAuthNm = normalize(rawAuthNm) || '기본';

                      const getIsDisabled = () => {
                        if (
                          targetRowAuthNm === '관리자' ||
                          targetRowAuthNm === '총괄관리자'
                        ) {
                          const forbiddenForAdmin = ['명예산업안전감독관'];
                          return forbiddenForAdmin.includes(posNm);
                        }

                        if (targetRowAuthNm === '기본' || !targetRowAuthNm) {
                          const forbiddenForBasic = [
                            '안전관리자',
                            '보건관리자',
                            '안전보건관리담당자',
                            '산업보건의',
                            '안전보건관리책임자',
                            '안전보건총괄책임자',
                            '중대재해 전담조직',
                          ];
                          return forbiddenForBasic.includes(posNm);
                        }

                        return false;
                      };

                      const isDisabled = getIsDisabled();
                      const isSelected = (
                        props.selectedPositions || []
                      ).includes(positionName);
                      const isNewlyAdded =
                        props.newlyAddedPositions?.includes(positionName);

                      const listItemEl = (
                        <ListItem
                          key={index}
                          disablePadding
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: isSelected
                              ? '#e3f2fd'
                              : isDisabled
                                ? '#fafafa'
                                : isNewlyAdded
                                  ? '#fff3e0'
                                  : 'transparent',
                            border: isSelected
                              ? '1px solid #1976d2'
                              : isDisabled
                                ? '1px solid #e0e0e0'
                                : isNewlyAdded
                                  ? '1px solid #ff9800'
                                  : '1px solid transparent',
                            opacity: isDisabled ? 0.6 : 1,
                          }}
                        >
                          <ListItemButton
                            onClick={() => {
                              if (isDisabled) return;
                              props.setSelectedPositions(
                                togglePositionSelection(
                                  props.selectedPositions,
                                  positionName
                                )
                              );
                            }}
                            disabled={isDisabled}
                            sx={{
                              py: 1.5,
                              px: 2,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flex: 1,
                              }}
                            >
                              {isSelected ? (
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 0.5,
                                    bgcolor: '#1976d2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                  }}
                                >
                                  <CheckIcon sx={{ fontSize: 18 }} />
                                </Box>
                              ) : (
                                <PersonIcon
                                  sx={{
                                    fontSize: 24,
                                    color: isDisabled
                                      ? '#bdbdbd'
                                      : 'rgba(0, 0, 0, 0.54)',
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  fontSize: '0.95rem',
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected
                                    ? '#1976d2'
                                    : isDisabled
                                      ? '#9e9e9e'
                                      : 'inherit',
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Box component="span">{positionName}</Box>
                                  {/* {isProtected && (
                                    <Box
                                      component="span"
                                      sx={{
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 999,
                                        fontSize: '0.72rem',
                                        fontWeight: 800,
                                        color: '#1565c0',
                                        bgcolor: '#E3F2FD',
                                        border: '1px solid #BBDEFB',
                                      }}
                                    >
                                      필수
                                    </Box>
                                  )} */}
                                  {isDisabled && (
                                    <Box
                                      component="span"
                                      sx={{
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 999,
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        color: '#757575',
                                        bgcolor: '#eeeeee',
                                        border: '1px solid #e0e0e0',
                                      }}
                                    >
                                      선택 불가
                                    </Box>
                                  )}
                                </Box>
                              </Typography>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      );

                      return isDisabled ? (
                        <Tooltip
                          key={index}
                          title={`'${targetRowAuthNm}' 권한은 ${posNm} 안전보건조직을 부여할 수 없습니다.`}
                          arrow
                          placement="left"
                        >
                          <Box component="span" sx={{ display: 'block' }}>
                            {listItemEl}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box
                          key={index}
                          component="span"
                          sx={{ display: 'block' }}
                        >
                          {listItemEl}
                        </Box>
                      );
                    })}
                  </List>
                </Box>
              </Box>
            }
          />
        </div>
      )}
      {props.modalToggle3 &&
        RenderCustDepPickerModal({
          open: props.modalToggle3,
          title: '총괄관리자 소속 사업장/부서 선택',
          confirmLabel: '선택',
          onConfirm: () => props.SaveLogic6(),
          onCancel: () => {
            props.setSelectedModalRowData3({ custCd: '', depCd: '' });
            props.setModalToggle3(false);
          },
          custItems: props.modalRowData3 ?? [],
          selectedCustCd: props.selectedModalRowData3?.custCd ?? '',
          setSelectedCustCd: (custCd) =>
            props.setSelectedModalRowData3((prev) => ({
              ...(prev || {}),
              custCd,
            })),
          depItems: props.modalRowData1 ?? [],
          selectedDepCd: props.selectedModalRowData3?.depCd ?? '',
          setSelectedDepCd: (depCd) =>
            props.setSelectedModalRowData3((prev) => ({ ...(prev || {}), depCd })),
        })}
      {props.modalToggle4 &&
        RenderCustDepPickerModal({
          open: props.modalToggle4,
          title: '총괄관리자 소속 사업장/부서 선택',
          confirmLabel: '선임',
          onConfirm: () => props.ConfirmAppointBusinessOwner(),
          onCancel: () => props.CancelAppointBusinessOwner(),
          custItems: searchInfo.custList || [],
          selectedCustCd: props.selectedCustCd ?? '',
          setSelectedCustCd: (custCd) => props.setSelectedCustCd?.(custCd || null),
          depItems: props.modalRowData1 ?? [],
          selectedDepCd: props.selectedDepCd ?? '',
          setSelectedDepCd: (depCd) => props.setSelectedDepCd?.(depCd || null),
        })}
    </Grid>
  );
}

export default MEM_BASIC;
